import {
  GetMissingFieldsLevelParameter,
  OnboardingMissingFields,
  VerificationLevel,
  VerificationLevels
} from "@wingspanhq/wingspan-api-sdk";
import {
  OnboardingContext,
  OnboardingModuleAdditionalInformation
} from "../types";
import { AdditionalInformation } from "../steps/AdditionalInformation";
import { EDD_FIELDS } from "../components/EDDQuestionnaire/utils";
import {
  FeatureFlags,
  useQueryFeatureFlags
} from "@hooks/useQueryFeatureFlags";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { queriesOnboarding } from "@query";
import {
  FlowStep,
  FlowStepStatus
} from "../../../shared/components/FlowSwitch/types";

export const getAdditionalInformationStepConfig = (
  _: OnboardingModuleAdditionalInformation,
  __: OnboardingContext,
  featureFlags: FeatureFlags | undefined,
  missingData: OnboardingMissingFields | undefined,
  verifications: VerificationLevels | undefined
): FlowStep | null => {
  const isMissingDataExist = EDD_FIELDS.some(field => {
    return missingData?.requiredFields?.includes(field.name);
  });

  if (!featureFlags?.onboardingEDDquestionnaire) {
    return null;
  }

  const slug = "additional-information";

  const isLoading = !missingData || !verifications;
  const isSuccess =
    verifications?.enhanced === VerificationLevel.VERIFIED &&
    !isMissingDataExist;
  const isPending =
    verifications?.enhanced === VerificationLevel.PENDING &&
    !isMissingDataExist;

  const status: FlowStepStatus = isLoading
    ? "Loading"
    : isSuccess
      ? "Complete"
      : isPending
        ? "Pending"
        : "None";

  if (status === "None" && !isMissingDataExist) {
    return null;
  }

  return {
    title: "Additional information",
    slug,
    component: AdditionalInformation,

    status,
    result: {
      Pending: {
        title: "Additional information pending review",
        type: "search",
        description:
          "Thank you for providing the necessary information. Check back on your homepage for updates.\n\nThe review period typically takes about one to two (1-2) business days, during this time you may not be able to send/receive payments or access banking features."
      },
      Complete: {
        title: "Additional information complete",
        description: "Thank you for providing the necessary information."
      }
    }
  };
};

export const useAdditionalInformationStep: UseCreateOnboardingStep<
  OnboardingModuleAdditionalInformation
> = modules => {
  const config = useOnboardingStepQueryConfig(
    modules,
    "additional_information"
  );
  const queryFeatureFlags = useQueryFeatureFlags(config);
  const queryMissingDataEnhanced = queriesOnboarding.getMissingFields.useQuery(
    { level: GetMissingFieldsLevelParameter.ENHANCED },
    config
  );
  const queryVerifications = queriesOnboarding.getOnboardingStatus.useQuery(
    undefined,
    { ...config, select: status => status.verifications ?? {} }
  );

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getAdditionalInformationStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        queryFeatureFlags,
        queryMissingDataEnhanced,
        queryVerifications
      ],
      loader: false
    });
};
